/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  ChartGroupInfo,
  ChartSeriesData,
  GroupAxisProps,
  TimeAxisProps,
  ValueFormats
} from '../UNSAFE_visTypes/chart';
import { VisTabularDatatip } from '../../PRIVATE_VisTabularDatatip';
import { TableRowData } from '../../PRIVATE_VisTabularDatatip';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { BundleType } from '#resources/nls/bundle';
import { generateAriaLabel, getElementData } from '#utils/UNSAFE_visUtils';
import { GroupLevelItem } from '#PRIVATE_Axis/groupAxis/hierarchicalUtils';
import type { RefObject } from 'preact';
import { TextDimensions } from '#hooks/PRIVATE_useTextDimensions';

/**
 * Returns the data required for the VisTabularDatatip to render.
 * @param activeInfo active item info.
 * @param series The array of chart series.
 * @param groups The array of chart groups.
 * @param valueFormats The array of chart series.
 * @param getDataItem function which returns chartItem
 */
export function getChartTabularData<K, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>>(
  data: D,
  seriesValue: string,
  groupValue: string[] | string,
  translations: BundleType,
  valueFormats?: ValueFormats,
  isTimeAxis?: boolean,
  defaultXFormatter?: (axisValue: number) => string,
  defaultYFormatter?: (value: number) => string,
  isSeriesAssociatedToY2?: boolean,
  hasXYValues?: boolean
) {
  let itemValue, itemX, itemY, itemWidthWeight, itemY2;
  const seriesLabel = translations.chart_labelSeries();
  const groupLabel = translations.chart_labelGroup();
  const valueLabel = translations.chart_labelValue();
  const xLabel = translations.chart_labelX();
  const yLabel = translations.chart_labelY();
  const widthWeightLabel = translations.chart_labelWidthWeight();
  const dataLabel = translations.chart_labelDataLabel();

  if (hasXYValues && 'y' in data) {
    const x = data.x;
    itemX = valueFormats?.x?.format ? valueFormats?.x?.format(x!) : defaultXFormatter?.(x!);

    const y = data.y;
    itemY = valueFormats?.y?.format ? valueFormats?.y?.format(y!) : defaultYFormatter?.(y!);
  } else if ('value' in data && data.value !== undefined) {
    const value = data.value;
    const formattedValue = valueFormats?.value?.format
      ? valueFormats.value.format(value)
      : defaultYFormatter?.(value);

    if (isSeriesAssociatedToY2) {
      itemY2 = valueFormats?.y2?.format ? valueFormats.y2.format(value) : formattedValue;
    } else {
      itemValue = formattedValue;
    }
  }
  if ('widthWeight' in data && data.widthWeight !== undefined) {
    const widthWeight = data.widthWeight;
    itemWidthWeight = valueFormats?.widthWeight?.format
      ? valueFormats?.widthWeight?.format?.(widthWeight)
      : defaultYFormatter?.(widthWeight);
  }
  const tableData: TableRowData[] = [];

  if (valueFormats?.series?.isDisplayed != false) {
    tableData.push({
      key: valueFormats?.series?.label || seriesLabel,
      value: seriesValue
    });
  }
  if (valueFormats?.group?.isDisplayed != false) {
    const isArray = Array.isArray(valueFormats?.group?.label);
    if (isTimeAxis) {
      const group = Array.isArray(groupValue) ? groupValue[0] : groupValue;
      tableData.push({
        key:
          (isArray ? valueFormats?.group?.label?.[0] : (valueFormats?.group?.label as string)) ||
          'Date',
        value: defaultXFormatter ? defaultXFormatter?.(new Date(group).getTime()) : group
      });
    } else {
      const groupsLabel = (
        isArray ? valueFormats?.group?.label : [valueFormats?.group?.label || groupLabel]
      ) as string[];
      const groups = Array.isArray(groupValue) ? groupValue : [groupValue];
      groups.forEach((value: string, index: number) => {
        tableData.push({
          key: groupsLabel[index],
          value: value
        });
      });
    }
  }
  if (valueFormats?.value?.isDisplayed != false) {
    if (hasXYValues) {
      tableData.push({
        key: valueFormats?.value?.label || valueFormats?.x?.label || xLabel,
        value: itemX!
      });
      tableData.push({
        key: valueFormats?.value?.label || valueFormats?.y?.label || yLabel,
        value: itemY!
      });
    } else {
      if (isSeriesAssociatedToY2) {
        tableData.push({
          key: valueFormats?.value?.label || valueFormats?.y2?.label || valueLabel,
          value: itemY2!
        });
      } else {
        tableData.push({
          key: valueFormats?.value?.label || valueLabel,
          value: itemValue!
        });
      }
    }
  }
  if (valueFormats?.widthWeight?.isDisplayed != false && itemWidthWeight) {
    tableData.push({
      key: valueFormats?.widthWeight?.label || widthWeightLabel,
      value: itemWidthWeight!
    });
  }
  if (valueFormats?.label?.isDisplayed != false && data.label) {
    tableData.push({
      key: valueFormats?.label?.label || dataLabel,
      value: data.label!
    });
  }
  return tableData;
}

export function getDefaultDatatipRenderer<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>(options: {
  series: ChartSeriesData<K, D>;
  groups: string[];
  translations: BundleType;
  valueFormats?: ValueFormats;
  timeAxisType?: 'mixedFrequency' | 'enabled' | 'skipGaps';
  defaultXFormatter?: (value: number) => string;
  defaultYFormatter?: (value: number) => string;
  defaultY2Formatter?: (value: number) => string;
  hasXYValues?: boolean;
}) {
  const isSeriesAssociatedToY2 = options.series.associatedYAxis === 'y2';
  const isMixedFrequency = options.timeAxisType === 'mixedFrequency';
  const isRegularTimeAxis =
    options.timeAxisType === 'enabled' || options.timeAxisType === 'skipGaps';

  return (context: { data: D }) => {
    const groupValue = options.groups;
    const timeValue = (
      isMixedFrequency ? context.data.x : isRegularTimeAxis ? groupValue : undefined
    ) as string;
    return {
      content: (
        <VisTabularDatatip
          tableData={getChartTabularData<K, D>(
            context.data,
            options.series.name || options.series.id.toString(),
            timeValue || options.groups,
            options.translations,
            options.valueFormats,
            options.timeAxisType != null,
            options.defaultXFormatter,
            isSeriesAssociatedToY2 ? options.defaultY2Formatter : options.defaultYFormatter,
            isSeriesAssociatedToY2,
            options.hasXYValues
          )}
        />
      ),
      borderColor: context.data.color || options.series.color,
      defaultContainer: 'enabled' as const
    };
  };
}

export function getAriaLabelGenerator<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>(
  series: ChartSeriesData<K, D>[],
  translations: BundleType,
  valueFormats: ValueFormats | undefined,
  leafGroupsInfo: GroupLevelItem[],
  xAxis: GroupAxisProps | TimeAxisProps | undefined,
  isDataXAxis: boolean,
  getGroupFromPath: (path: number[]) => string[],
  xDefaultLabelFormatter?: (value: number) => string,
  yDefaultLabelFormatter?: (value: number) => string,
  y2DefaultLabelFormatter?: (value: number) => string
) {
  const getItemAriaLabel = (
    data: D,
    seriesIndex: number,
    groupIndex: number,
    isSelectionEnabled: boolean,
    isSelected: boolean,
    isDrillable: boolean
  ) => {
    let accLabel = data.accessibleLabel;
    if (!accLabel) {
      const _series = series[seriesIndex];
      const isAssociatedToY2 = _series.associatedYAxis === 'y2';
      const groupValue = getGroupFromPath(leafGroupsInfo[groupIndex].path);
      const timeValue = (
        (xAxis as TimeAxisProps)?.timeAxisType === 'mixedFrequency'
          ? data.x
          : (xAxis as TimeAxisProps)?.timeAxisType === 'enabled'
          ? groupValue
          : undefined
      ) as string;

      const tababularData = getChartTabularData(
        data,
        _series.name || _series.id.toString(),
        timeValue || groupValue,
        translations,
        valueFormats,
        (xAxis as TimeAxisProps)?.timeAxisType != null,
        xDefaultLabelFormatter,
        isAssociatedToY2 ? y2DefaultLabelFormatter : yDefaultLabelFormatter,
        isDataXAxis
      );
      accLabel = tababularData
        .map(({ key, value }) => `${key ? key + ':' : ''} ${value}`)
        .join(', ');
    }
    return generateAriaLabel(translations, accLabel, {
      isSelected: !isSelectionEnabled ? undefined : isSelected,
      isDrillable
    });
  };

  return getItemAriaLabel;
}

export const getTextAndBoundsUtil = (
  target?: EventTarget | null,
  rootRef?: RefObject<Element>,
  xAxisTitle?: string,
  yAxisTitle?: string,
  y2AxisTitle?: string,
  xTitleDimention?: TextDimensions,
  yTitleDimention?: TextDimensions,
  y2TitleDimention?: TextDimensions,
  xAxisTickLabels?: any[],
  focusedGroupInfo?: ChartGroupInfo
) => {
  if (target != rootRef?.current) {
    const dataSet = getElementData(rootRef!, target as HTMLElement);
    const text = dataSet?.['ojPrivateText']!;

    if (!text) {
      return;
    }

    if (text === xAxisTitle) {
      return {
        text,
        bounds: xTitleDimention
      };
    } else if (text === yAxisTitle) {
      return {
        text,
        bounds: yTitleDimention
      };
    } else if (text === y2AxisTitle) {
      return {
        text,
        bounds: y2TitleDimention
      };
    } else if (dataSet?.['ojPrivateGroupIndex'] && dataSet?.['ojPrivateLevelIndex']) {
      return {
        // @ts-ignore
        bounds: xAxisTickLabels.find(
          ({ dataProps }) =>
            dataProps['data-oj-private-level-index'] === dataSet['ojPrivateLevelIndex'] &&
            dataProps['data-oj-private-group-index'] === dataSet['ojPrivateGroupIndex']
        ).dims,
        text: text!
      };
    }
  }

  if (target === rootRef?.current && focusedGroupInfo?.isFocusVisible) {
    const dimsAndText = getTextAndDimsInfo(
      xAxisTickLabels,
      focusedGroupInfo.groupIndex,
      focusedGroupInfo.levelIndex
    );
    return {
      bounds: dimsAndText?.dims,
      text: dimsAndText?.text
    };
  }
  return;
};

export const getTextAndDimsInfo = (
  tickLabels?: any[],
  groupIndex?: number,
  levelIndex?: number
) => {
  return tickLabels?.find(
    (tickLabel) =>
      tickLabel?.dataProps?.['data-oj-private-group-index'] == groupIndex &&
      tickLabel?.dataProps?.['data-oj-private-level-index'] == levelIndex &&
      tickLabel?.isTruncated
  );
};
